import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const CARDS = [
  {
    channel: "primary",
    title: "Primary",
    sub: "Primary Investment Opportunities",
    points: ["New inventory", "Current primary pricing", "Multiple configurations", "Payment options"],
    img: "/eon-tower.webp",
    cta: "View Primary",
    testId: "view-primary-btn",
  },
  {
    channel: "resale",
    title: "Resale",
    sub: "Ready-to-Move Opportunities",
    points: ["Owner resale", "Fitted offices", "Investment opportunities", "Potential pre-leased assets"],
    img: "/eon-plaza.webp",
    cta: "View Resale",
    testId: "view-resale-btn",
  },
];

export default function PrimaryResale({ onSelect }) {
  return (
    <section id="primary-resale" data-testid="primary-resale-section" className="py-28 sm:py-32 bg-[#0D0F12]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Two Ways In</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]">
            Primary <span className="text-[#9CA3AF] font-light italic">or</span> Resale<span className="gold-text">.</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm sm:text-base text-[#9CA3AF] leading-relaxed">
            We help you explore EON opportunities across new primary inventory and owner resale. Choose the route
            that fits your budget and timeline.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {CARDS.map((c, i) => (
            <motion.article
              key={c.channel}
              data-testid={`channel-card-${c.channel}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, delay: 0.12 * i, ease: [0.16, 1, 0.3, 1] }}
              className="group relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-[#D4AF37]/40 transition-colors duration-500 min-h-[460px] flex flex-col justify-end"
            >
              <img
                src={c.img}
                alt={`${c.title} opportunities at EON`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/70 to-[#0B0C0E]/20" />
              <div className="relative p-8 sm:p-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{c.sub}</div>
                <h3 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-[#F3F4F6]">{c.title}</h3>
                <ul className="mt-5 space-y-2.5">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm text-[#D1D5DB]">
                      <Check size={14} className="text-[#D4AF37] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={c.testId}
                  onClick={() => onSelect?.(c.channel)}
                  className="mt-7 px-7 py-3.5 bg-[#D4AF37] text-[#0B0C0E] font-sans text-xs font-semibold uppercase tracking-[0.18em] rounded-full hover:bg-[#F3E5AB] hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-all duration-300 inline-flex items-center gap-2"
                >
                  {c.cta}
                  <ArrowUpRight size={14} />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
